import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import api, { formatApiError, fileUrl } from "../api";
import { byProductName, fmtQty } from "../lib/utils";
import { BrutalCard, StockBadge, BrutalButton } from "../components/Brutal";
import { PriceTags } from "./InventoryPage";
import RestockRequestDialog from "../components/RestockRequestDialog";
import { SupplierCatalog } from "../components/SupplierCatalog";
import AssignProductDialog from "../components/AssignProductDialog";
import { Warning, Package, PaperPlaneTilt, CheckCircle, HandPalm, Hash, Receipt, CurrencyDollar, ArrowUUpLeft, Storefront, Tag, Flask } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useAuth } from "../AuthContext";
import { ProductTitle } from "../components/ProductTitle";
import SaleRequestDialog from "../components/SaleRequestDialog";
import UnifiedRequestDialog from "../components/UnifiedRequestDialog";
import { DistributorRecentActivity } from "../components/DistributorRecentActivity";
import { GettingStartedCard } from "../components/GettingStartedCard";
import { ConvertStockDialog } from "../components/ConvertStockDialog";
export default function DistributorDashboard() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [requests, setRequests] = useState([]);
  const [saleRequests, setSaleRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [requestingFor, setRequestingFor] = useState(null);
  const [consigningFor, setConsigningFor] = useState(null);
  const [showSaleRequest, setShowSaleRequest] = useState(false);
  const [unifiedRequest, setUnifiedRequest] = useState(null); // v414.73: null | 'restock' | 'sale'
  const [directSaleFor, setDirectSaleFor] = useState(null); // v401.8 per-card $ Direct Sale
  const [convertingFor, setConvertingFor] = useState(null); // v414.15 producer conversion

  const load = useCallback(async () => {
    try {
      const [p, r, sr] = await Promise.all([
        api.get("/products"),
        api.get("/restock-requests", { params: { status: "pending" } }),
        api.get("/sale-requests", { params: { status: "pending" } }),
      ]);
      setProducts(p.data);
      setRequests(r.data);
      setSaleRequests(sr.data.items || []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // v404.7 live sync — the dashboard reads straight from the ReUP database;
  // poll every 30s and refetch on tab focus so admin-side moves show up
  // without a manual refresh.
  useEffect(() => {
    const iv = setInterval(load, 30000);
    const onFocus = () => document.visibilityState === "visible" && load();
    document.addEventListener("visibilitychange", onFocus);
    window.addEventListener("focus", onFocus);
    return () => {
      clearInterval(iv);
      document.removeEventListener("visibilitychange", onFocus);
      window.removeEventListener("focus", onFocus);
    };
  }, [load]);

  const adjust = async (id, delta) => {
    // optimistic update
    setProducts((prev) => prev.map((p) => p.id === id ? { ...p, current_stock: Math.max(0, p.current_stock + delta) } : p));
    try {
      await api.post(`/products/${id}/adjust`, { delta });
      // refresh requests in case auto-trigger fired
      const r = await api.get("/restock-requests", { params: { status: "pending" } });
      setRequests(r.data);
    } catch (e) {
      toast.error(formatApiError(e));
      load();
    }
  };

  const pendingProductIds = new Set(requests.map((r) => r.product_id));
  const lowItems = products.filter((p) => p.starting_stock > 0 && (p.current_stock / p.starting_stock) < 0.4);

  // v401.8: self-return — push my own stock UP one level (sub -> parent).
  const selfReturn = async (p) => {
    const raw = window.prompt(t("distributor_dashboard.return_prompt", { name: p.name, max: p.current_stock }), "1");
    if (raw === null) return;
    const qty = parseInt(raw, 10);
    if (!Number.isFinite(qty) || qty <= 0) {
      toast.error(t("distributor_dashboard.return_invalid"));
      return;
    }
    try {
      await api.post("/products/return", { distributor_id: user.id, product_id: p.id, quantity: qty });
      toast.success(t("distributor_dashboard.return_done", { n: qty, name: p.name }));
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div className="space-y-6" data-testid="distributor-dashboard">
      <div>
        <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight">{t("distributor_dashboard.title")}</h1>
        <p className="text-sm sm:text-base text-zinc-600 mt-1">
          {t("distributor_dashboard.subtitle")}
        </p>
      </div>

      {user?.distributor_code && (
        <div
          className="border-2 border-[#c9a24a] bg-[#fbf6e7] rounded-xl p-4 sm:p-5 flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-6"
          data-testid="my-distributor-code"
        >
          <div className="flex items-center gap-3 shrink-0">
            <div className="w-11 h-11 rounded-lg bg-[#c9a24a] text-white flex items-center justify-center">
              <Hash size={22} weight="bold" />
            </div>
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-wider text-[#8a6a1c]">{t("dist_code.your_code")}</p>
              <p className="text-3xl font-bold font-mono tracking-widest text-zinc-950 leading-none">{user.distributor_code}</p>
            </div>
          </div>
          <p className="text-sm text-[#8a6a1c] font-medium flex-1">{t("dist_code.etransfer_hint")}</p>
          {/* v406.1 — one-tap storefront access right from the dashboard */}
          <div className="flex gap-2 shrink-0 flex-wrap">
            <BrutalButton size="sm" onClick={() => window.open(`/${user.distributor_code}`, "_blank")}
              data-testid="dash-view-storefront">
              <Storefront size={14} weight="bold" /> {t("distributor_dashboard.view_storefront")}
            </BrutalButton>
            <BrutalButton size="sm" variant="secondary" onClick={() => navigate("/my-store")}
              data-testid="dash-manage-store">
              <Tag size={14} weight="bold" /> {t("distributor_dashboard.manage_store")}
            </BrutalButton>
          </div>
        </div>
      )}

      {/* v406.9 — Getting Started checklist (auto-hides once complete) */}
      <GettingStartedCard />

      {/* v414.73 — ONE request box: restock, new products or customer sales */}
      <div className="border-2 border-zinc-950 rounded-xl p-4 sm:p-5 flex flex-col sm:flex-row sm:items-center gap-3 justify-between" data-testid="unified-request-panel">
        <div>
          <h3 className="font-semibold text-base flex items-center gap-2">
            <PaperPlaneTilt size={18} weight="bold" className="text-[#8a6a1c]" />
            {t("unified_request.panel_title")}
          </h3>
          <p className="text-sm text-zinc-600 mt-0.5">{t("unified_request.panel_hint")}</p>
        </div>
        <BrutalButton onClick={() => setUnifiedRequest("restock")} data-testid="unified-request-open">
          {t("unified_request.open_btn")}
        </BrutalButton>
      </div>

      {/* v401.1 Payment Identification — build the sale BEFORE e-transferring */}
      <div className="border-2 border-zinc-950 rounded-xl p-4 sm:p-5" data-testid="sale-request-panel">
        <div className="flex flex-col sm:flex-row sm:items-center gap-3 justify-between">
          <div>
            <h3 className="font-semibold text-base flex items-center gap-2">
              <Receipt size={18} weight="bold" className="text-[#8a6a1c]" />
              {t("sale_request.panel_title")}
            </h3>
            <p className="text-sm text-zinc-600 mt-0.5">{t("sale_request.panel_hint")}</p>
          </div>
          <BrutalButton onClick={() => setUnifiedRequest("sale")} data-testid="new-sale-request-btn">
            {t("sale_request.new_btn")}
          </BrutalButton>
        </div>
        {saleRequests.length > 0 && (
          <div className="mt-3 space-y-2">
            {saleRequests.map((sr) => (
              <div key={sr.id} className="flex items-center justify-between gap-2 rounded-lg bg-[#fbf6e7] border border-[#c9a24a]/60 px-3 py-2" data-testid={`pending-sale-request-${sr.id}`}>
                <div className="min-w-0">
                  <span className="font-mono font-bold">{sr.code}</span>
                  <span className="text-xs text-zinc-600 ml-2 truncate">
                    {sr.items.map((it) => `${it.quantity}x ${it.product_name}`).join(", ")}
                  </span>
                </div>
                <span className="font-semibold tabular-nums shrink-0">${sr.total_amount.toFixed(2)}</span>
              </div>
            ))}
            <p className="text-[11px] text-[#8a6a1c] font-medium">{t("sale_request.memo_instruction")}</p>
          </div>
        )}
      </div>

      {/* v409.7 Supplier catalog — full price list + order requests */}
      <div className="border-2 border-[#c9a24a] rounded-xl p-4 sm:p-5" data-testid="new-product-panel">
        <div>
          <h3 className="font-semibold text-base flex items-center gap-2">
            <Package size={18} weight="bold" className="text-[#8a6a1c]" />
            {t("restock.catalog_title")}
          </h3>
          <p className="text-sm text-zinc-600 mt-0.5">{t("restock.catalog_hint")}</p>
        </div>
        {requests.filter((r) => r.new_product).length > 0 && (
          <div className="mt-3 flex flex-wrap gap-2" data-testid="new-product-pending-chips">
            {requests.filter((r) => r.new_product).map((r) => (
              <span
                key={r.id}
                className="inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-full bg-[#fbf6e7] border border-[#c9a24a]/60 text-[#8a6a1c]"
                data-testid={`new-product-pending-${r.id}`}
              >
                <CheckCircle size={12} weight="fill" />
                {r.product_name}{r.quantity ? ` ×${r.quantity}` : ""}
              </span>
            ))}
          </div>
        )}
        <SupplierCatalog pendingRequests={requests} onRequested={load} />
      </div>

      {lowItems.length > 0 && (
        <div className="border border-red-200 bg-red-50 text-red-900 rounded-xl p-4 sm:p-5 flex items-start gap-3" data-testid="dist-low-banner">
          <Warning size={22} weight="fill" className="text-red-600 mt-0.5 shrink-0" />
          <div>
            <h3 className="font-semibold text-base">{t("distributor_dashboard.reorder_time")}</h3>
            <p className="text-sm text-red-800 mt-0.5">{t("distributor_dashboard.running_low", { count: lowItems.length })}</p>
          </div>
        </div>
      )}

      {loading ? (
        <p className="text-sm text-zinc-500">{t("common.loading")}</p>
      ) : products.length === 0 ? (
        <div className="bg-zinc-50 border border-zinc-200 rounded-xl p-4 text-sm text-zinc-600">
          {t("distributor_dashboard.no_products")}
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-5">
          {[...products].sort(byProductName).map((p) => (
            <BigProductCard
              key={p.id}
              product={p}
              onAdjust={adjust}
              requested={pendingProductIds.has(p.id)}
              onRequestRestock={() => setRequestingFor(p)}
              onConsign={() => setConsigningFor(p)}
              onDirectSale={() => setDirectSaleFor(p)}
              onReturn={() => selfReturn(p)}
              onConvert={user?.can_convert ? () => setConvertingFor(p) : null}
            />
          ))}
        </div>
      )}

      {requestingFor && (
        <RestockRequestDialog
          product={requestingFor}
          onClose={() => setRequestingFor(null)}
          onSubmitted={load}
        />
      )}

      <DistributorRecentActivity />

      {consigningFor && (
        <AssignProductDialog
          product={consigningFor}
          onClose={() => setConsigningFor(null)}
          onAssigned={load}
        />
      )}

      {showSaleRequest && (
        <SaleRequestDialog
          onClose={() => { setShowSaleRequest(false); load(); }}
        />
      )}

      {unifiedRequest && (
        <UnifiedRequestDialog
          initialType={unifiedRequest}
          onClose={() => setUnifiedRequest(null)}
          onDone={load}
        />
      )}

      {directSaleFor && (
        <SaleRequestDialog
          initialProductId={directSaleFor.id}
          onClose={() => { setDirectSaleFor(null); load(); }}
        />
      )}

      {convertingFor && (
        <ConvertStockDialog
          product={convertingFor}
          holderId={user?.id}
          onClose={() => setConvertingFor(null)}
          onConverted={() => { setConvertingFor(null); load(); }}
        />
      )}
    </div>
  );
}

// v401.7 UNIVERSAL MEDIUM CARD: same tight layout as the admin inventory.
function BigProductCard({ product, onAdjust, requested, onRequestRestock, onConsign, onDirectSale, onReturn, onConvert }) {
  const { t } = useTranslation();
  const percent = product.starting_stock > 0 ? (product.current_stock / product.starting_stock) * 100 : 0;
  const low = percent < 40;
  const isInjectable = (product.category || "Injectable") === "Injectable";
  return (
    <div
      data-testid={`dist-card-${product.id}`}
      className={`bg-white border-2 ${low ? "border-red-300" : "border-black"} rounded-xl p-3 shadow-sm`}
    >
      <div className="flex gap-3">
        {product.photo_path && (
          <img
            src={fileUrl(product.photo_path)}
            alt={product.name}
            className="w-14 h-14 border border-zinc-200 rounded-lg object-cover shrink-0"
            crossOrigin="use-credentials"
          />
        )}
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <ProductTitle name={product.name} id={product.id} as="h2" className="text-base font-semibold tracking-tight truncate" />
            <StockBadge percent={percent} />
          </div>
          <div className="mt-1 flex items-baseline gap-1.5">
            <span className="text-xl font-semibold leading-none">{fmtQty(product.current_stock)}</span>
            <span className="text-xs font-medium text-zinc-600">/ {fmtQty(product.starting_stock)} {product.unit || t("common.units")}</span>
          </div>
          <div className="h-1.5 mt-1.5 bg-zinc-100 border border-zinc-200 rounded-full overflow-hidden">
            <div className={`h-full ${low ? "bg-red-600" : "bg-[#c9a24a]"}`} style={{ width: `${Math.min(100, Math.max(0, percent))}%` }} />
          </div>
          <PriceTags product={product} className="!mt-1.5" />
        </div>
      </div>

      <div className="mt-2 flex gap-2">
        <button
          data-testid={`dist-decrement-${product.id}`}
          onClick={() => onAdjust(product.id, -1)}
          disabled={product.current_stock <= 0}
          className="flex-1 h-11 bg-white text-black border border-zinc-300 rounded-lg shadow-sm hover:-translate-y-px transition-all flex items-center justify-center gap-1.5 font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <span className="text-2xl leading-none">−</span>
          <span className="text-xs">{t("distributor_dashboard.sold_one")}</span>
        </button>
        <button
          data-testid={`dist-increment-${product.id}`}
          onClick={() => onAdjust(product.id, 1)}
          className="flex-1 h-11 bg-[#c9a24a] text-zinc-950 border border-[#c9a24a] rounded-lg hover:bg-[#b8923f] transition-colors flex items-center justify-center gap-1.5 font-semibold"
        >
          <span className="text-2xl leading-none">+</span>
          <span className="text-xs">{t("distributor_dashboard.got_more")}</span>
        </button>
      </div>

      <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-2">
        {requested ? (
          <div
            data-testid={`restock-requested-${product.id}`}
            className="h-9 bg-zinc-100 border border-zinc-200 rounded-lg flex items-center justify-center gap-2 font-medium text-xs text-zinc-700"
          >
            <CheckCircle size={14} weight="fill" /> {t("distributor_dashboard.restock_requested")}
          </div>
        ) : (
          <button
            data-testid={`request-restock-${product.id}`}
            onClick={onRequestRestock}
            className={`h-9 ${low ? "bg-red-600 text-white border-red-600 hover:bg-red-700" : "bg-white text-zinc-900 border-zinc-200 hover:bg-zinc-50"} border rounded-lg transition-colors flex items-center justify-center gap-2 font-medium text-xs`}
          >
            <PaperPlaneTilt size={14} weight="bold" />
            {t("distributor_dashboard.request_restock")}
          </button>
        )}
        <button
          data-testid={`consign-${product.id}`}
          onClick={onConsign}
          disabled={product.current_stock <= 0}
          className="h-9 bg-white text-zinc-900 border border-zinc-200 rounded-lg hover:bg-zinc-50 transition-colors flex items-center justify-center gap-2 font-medium text-xs disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <HandPalm size={14} weight="bold" />
          {t("distributor_dashboard.give_to_someone")}
        </button>
        <button
          data-testid={`direct-sale-${product.id}`}
          onClick={onDirectSale}
          disabled={product.current_stock <= 0}
          className="h-9 bg-[#fbf6e7] text-[#8a6a1c] border border-[#c9a24a] rounded-lg hover:bg-[#f5ecd2] transition-colors flex items-center justify-center gap-2 font-semibold text-xs disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <CurrencyDollar size={14} weight="bold" />
          {t("sale_request.direct_sale")}
        </button>
        <button
          data-testid={`self-return-${product.id}`}
          onClick={onReturn}
          disabled={product.current_stock <= 0}
          className="h-9 bg-white text-zinc-900 border border-zinc-200 rounded-lg hover:bg-zinc-50 transition-colors flex items-center justify-center gap-2 font-medium text-xs disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <ArrowUUpLeft size={14} weight="bold" />
          {t("return.button")}
        </button>
        {onConvert && isInjectable && (
          <button
            data-testid={`convert-${product.id}`}
            onClick={onConvert}
            disabled={product.current_stock <= 0}
            className="h-9 bg-[#101012] text-[#c9a24a] border border-[#c9a24a] rounded-lg hover:bg-zinc-800 transition-colors flex items-center justify-center gap-2 font-semibold text-xs disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Flask size={14} weight="bold" />
            {t("convert.button")}
          </button>
        )}
      </div>
    </div>
  );
}
